import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { styled } from 'linaria/react';
import { cx } from 'linaria';
import { useQuery } from '@apollo/react-hooks';
import t from '@jetshop/intl';
import { useIntl } from '@jetshop/intl';
import Image from '@jetshop/ui/Image/Image';
import VariantSearch from './VariantSearch.gql';
import { theme } from '../Theme';
import Button from '../ui/Button';
import StockOrb from './StockStatus/StockOrb';
import useLocalStorage from '../../utils/useLocalStorage';

const VariantsContainer = styled('div')`
  h2 {
    font-size: 12px;
    font-weight: 400;
    letter-spacing: -0.02rem;

    span.color {
      text-transform: capitalize;
    }
  }
  h2:first-child {
    border-top: 1px solid #e0e0e0;
    padding-top: 20px !important;
    width: 100%;
  }

  .variants {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .variant-section {
    margin-top: 20px;
  }

  .variant-select-title {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 8px;
  }

  .variant {
    letter-spacing: -0.04em;
    &.out-of-stock {
      opacity: 0.4;
    }
  }

  button.variant {
    padding: 4px 0 6px;
    border-radius: 0;
  }

  .variant-link {
    span.label {
      border-radius: 100%;
      width: 18px;
      height: 18px;
      display: block;
      margin-top: 4px;
    }
    .add-border {
      border: 1px solid #e0e0e0;
    }
    .variant-link:hover [data-flight-image-container] {
      border-color: ${theme.colors.black};
    }
  }
  .size-guide-wrapper > button {
    margin: 1.25rem 0 0.5rem;
    font-weight: 600;
  }
`;
const ColorWrapper = styled('div')`
  &.active {
    border-bottom: 1px solid black;
    padding-bottom: 8px;
  }
`;

const ColorVariants = ({ product }) => {
  const model = product?.articleNumber?.substring(0, 6);

  const { data } = useQuery(VariantSearch, {
    variables: { articleNumber: model },
    skip: model ? false : true
  });

  if (!data) return null;

  const variants = data?.search?.products?.result;

  // Filtering out those results where the model substring appears not
  // at the beginning of the article number
  const filteredColorVariants = variants
    ? variants
        .filter(
          v =>
            v.articleNumber.substring(0, 6) === model &&
            v.images.length > 0 &&
            v.stockStatus.buyable
        )
        .sort((a, b) => a.articleNumber - b.articleNumber)
    : null;

  // There will be always at least one result, the product itself
  if (!filteredColorVariants || filteredColorVariants.length < 1) return null;

  // Finding a variant name to compare and find out the name of the color
  // This methods compare the product name parts word by word until
  // there is no match (which will be the color at the end of the name)
  const activeColor = product.customFields
    .find(({ key }) => key === 'color1')
    ?.stringValue.replace('_', ' ');

  return (
    <div>
      <div className="variant-select-title">
        <h2>
          {t('Color')}
          {': '}
          {activeColor && (
            <span className="color">{activeColor.toLowerCase()}</span>
          )}
        </h2>
      </div>
      <div className="variants">
        {filteredColorVariants.map(variant => (
          <ImageVariantButton
            key={variant.articleNumber}
            isActive={product.articleNumber === variant.articleNumber}
            {...variant}
          />
        ))}
      </div>
    </div>
  );
};

const ImageVariantButton = ({
  images,
  name,
  primaryRoute,
  customFields,
  isActive
}) => {
  const color = customFields.find(({ key }) => key === 'site_hex')?.stringValue;
  const addBorder = color === '#FFFFFF' || color === '#FFFFF';

  return (
    <ColorWrapper className={isActive ? 'active' : ''}>
      <Link
        className={cx('variant-link', 'variant', isActive ? 'active' : '')}
        to={primaryRoute.path}
      >
        <span
          className={cx('label', addBorder ? 'add-border' : '')}
          style={{
            backgroundColor: color
          }}
        ></span>
      </Link>
    </ColorWrapper>
  );
};

const sizeOrder = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

const Variants = ({
  product,
  variantHandler,
  showValidation,
  disableOutOfStock
}) => {
  return product.variants.options.map((option, index) => {
    let showValidationMessage = false;
    if (
      showValidation &&
      variantHandler.getMissingOptions()?.includes(option)
    ) {
      showValidationMessage = true;
    }
    return (
      <VariantButtons
        option={option}
        variantHandler={variantHandler}
        key={option.name}
        showValidationMessage={showValidationMessage}
        disableOutOfStock={disableOutOfStock}
        doNotDisable={index === 0}
        product={product}
      />
    );
  });
};

function VariantButtons({
  option,
  variantHandler,
  showValidationMessage,
  disableOutOfStock,
  doNotDisable,
  product
}) {
  const { getSelectedValue, validateSelection, selectValue } = variantHandler;
  const selectedValue = getSelectedValue(option);
  const t = useIntl();
  const [remeberedSize, rememberSize] = useLocalStorage('sizes');
  const clickHandler = value => {
    if (!isNaN(value) && rememberSize) {
      // save preference in localstorage
      rememberSize(value);
    }
    selectValue(value, option);
  };
  useEffect(() => {
    if (
      remeberedSize &&
      option.values.includes(remeberedSize) &&
      validateSelection(remeberedSize, option) !== 'outOfStock'
    ) {
      selectValue(remeberedSize, option);
    }
  }, [remeberedSize, selectValue, option, validateSelection]);

  if (option.values?.length < 1) {
    return null;
  }

  const sortedValues = sizeOrder.includes(option.values[0])
    ? // if S, M, L...
      [...option.values].sort(
        (a, b) =>
          sizeOrder.indexOf(a.toUpperCase()) -
          sizeOrder.indexOf(b.toUpperCase())
      )
    : !isNaN(parseInt(option.values[0].split('_')[0]))
    ? // if number
      [...option.values].sort(
        (a, b) => parseInt(a.split('_')[0]) - parseInt(b.split('_')[0])
      )
    : // if text
      [...option.values].sort();

  return (
    <div
      className={cx(
        sortedValues.length > 1 && 'variant-section',
        showValidationMessage ? 'invalid' : ''
      )}
    >
      <div className="variant-select-title">
        {sortedValues.length > 1 ? (
          <h2>
            {showValidationMessage && <StockOrb className="missingVariant" />}
            {t('Select {optionName}', {
              optionName: option.name.toLowerCase()
            })}
          </h2>
        ) : null}
      </div>
      <div className="variants">
        {sortedValues.length > 1
          ? sortedValues.map(value => {
              const validation = validateSelection(value, option);
              return (
                <Button
                  data-testid={option.name + value}
                  key={option.name + value}
                  onClick={() => clickHandler(value)}
                  disabled={
                    !doNotDisable &&
                    (validation === 'invalid' ||
                      (disableOutOfStock && validation === 'outOfStock'))
                  }
                  transparent={value !== selectedValue}
                  underline={value === selectedValue}
                  // aria-pressed={value === selectedValue}
                  className={cx(
                    'variant',
                    validation === 'outOfStock' ? 'out-of-stock' : ''
                  )}
                >
                  {value.replace('_', '/')}
                </Button>
              );
            })
          : null}
      </div>
    </div>
  );
}

export function VariantSelector({
  className,
  product,
  variantHandler,
  showValidation
}) {
  return (
    <VariantsContainer className={className}>
      <ColorVariants product={product} />
      <Variants
        product={product}
        variantHandler={variantHandler}
        showValidation={showValidation}
      />
    </VariantsContainer>
  );
}
